'use client';

import Link from 'next/link';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { Box, Skeleton, Typography } from '@mui/material';

import { useT } from '@/hooks';

import { ROUTES } from '@/constants';

interface PortfolioBalancesProps {
  portfolioValue: number;
  cashBalance: number;
  isLoading?: boolean;
}

interface BalanceCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  isClickable?: boolean;
  href?: string;
  showDivider?: boolean;
  isLoading?: boolean;
}

const BalanceCard = ({
  icon,
  label,
  value,
  isClickable,
  href,
  showDivider = true,
  isLoading,
}: BalanceCardProps) => {
  const content = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { md: 0.75, lg: 1 },
        pr: isClickable ? { md: 1, lg: 1.5 } : 0,
        borderRight: isClickable && showDivider ? '1px solid' : 'none',
        borderColor: 'divider',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'opacity 0.2s ease',
        '&:hover': isClickable ? { opacity: 0.8 } : {},
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: { md: 28, lg: 32 },
          height: { md: 28, lg: 32 },
          borderRadius: '8px',
          bgcolor: 'success.bg',
          color: '#22c55e',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: { md: 60, lg: 70 } }}>
        <Typography
          sx={{
            fontSize: { md: '0.625rem', lg: '0.6875rem' },
            fontWeight: 500,
            color: 'text.secondary',
            lineHeight: 1.2,
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: { md: '0.8125rem', lg: '0.9375rem' },
            fontWeight: 700,
            color: '#22c55e',
            lineHeight: 1.2,
            mt: 0.25,
          }}
        >
          {isLoading ? <Skeleton variant="text" width={50} height={18} /> : value}
        </Typography>
      </Box>
    </Box>
  );

  if (isClickable && href) {
    return (
      <Link href={href} style={{ textDecoration: 'none' }}>
        {content}
      </Link>
    );
  }

  return content;
};

export const PortfolioBalances = ({
  portfolioValue,
  cashBalance,
  isLoading,
}: PortfolioBalancesProps) => {
  const { t } = useT();

  const portfolioIcon = (
    <AccountBalanceWalletIcon sx={{ fontSize: { md: '1rem', lg: '1.125rem' } }} />
  );
  const cashIcon = <AttachMoneyIcon sx={{ fontSize: { md: '1rem', lg: '1.125rem' } }} />;

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        gap: { md: 1, lg: 1.5 },
        mr: { md: 0.5, lg: 1 },
        px: { md: 1.5, lg: 2 },
        py: { md: 0.75, lg: 1 },
        borderRadius: '12px',
        bgcolor: 'background.subtle',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: 'background.hover',
        },
      }}
    >
      <BalanceCard
        icon={portfolioIcon}
        label={t('nav.portfolio')}
        value={`$${portfolioValue.toFixed(2)}`}
        isClickable={!isLoading}
        href={ROUTES.PORTFOLIO}
        isLoading={isLoading}
      />
      <BalanceCard
        icon={cashIcon}
        label={t('nav.cash')}
        value={`$${cashBalance.toFixed(2)}`}
        isClickable={!isLoading}
        href={ROUTES.PORTFOLIO}
        showDivider={false}
        isLoading={isLoading}
      />
    </Box>
  );
};
